import { startCase, camelCase } from "lodash";
import { useMemo } from "react";
import moment from "moment";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { Modal } from "@kep-ui-kit/ui-components";
import { Scrollbars } from 'react-custom-scrollbars-2';
import { Chart } from "react-google-charts";
import { StyledText, StyledTitle } from "@selfserve/react-ui";

const StyledModal = styled(Modal)(({ theme }: { theme?: any }) => ({
  '.MuiPaper-root': {
    width: '100%',
    height: '100%',
    maxWidth: 'calc(100% - 200px)',
  },
}));

const StyledModalContainer = styled('div')(
  ({ theme }: { theme?: any }) => ({
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(3),
  })
);

const StyledSummaryTitle = styled(StyledTitle)(
  ({ theme }: { theme?: any }) => ({
    margin: `0 0 ${theme.spacing(2)} 0`,
  })
);

const StyledSummaryMrr = styled(StyledText)(
  ({ theme }: { theme?: any }) => ({
    fontSize: theme.typography.pxToRem(18),
    span: {
      color: 'rgb(148, 131, 245)',
    },
  })
);

interface Props {
  isOpen: boolean;
  onClose: () => void;
  summary: any;
};

export const AdminSummaryChartModal = ({ isOpen, onClose, summary }: Props) => {
  const { t } = useTranslation();
  const { createdAt, mrr, ...rest } = summary;
  const createdAtChart = useMemo(() => {
    const data = [
      [t('common.day'), t('common.count')],
      ...Object.entries(createdAt || {})
        .sort(([a], [b]) => {
          const firstDate = +moment(a).format('YYYYMMDD');
          const secondDate = +moment(b).format('YYYYMMDD');
          return firstDate - secondDate;
        })
        .map(([date, value]) => {
          return [
            moment(date).format('MMM DD, YYYY'),
            value as number,
          ];
        })
    ];
    const options = { chart: { title: t('common.created-at') }, legend: { position: 'none' } };
    return { options, data };
  }, [createdAt]);
  const otherCharts = useMemo(() => {
    return Object.entries(rest).map(([chartName, values]) => {
      const options = { title: startCase(camelCase(chartName)) };
      const data = [
        ['', ''],
        ...Object.entries(values || {}).map(([name, value]) => [name, value]),
      ];
      return { options, data };
    });
  }, [rest]);

  return (
    <StyledModal
      open={isOpen}
      onClose={onClose}
    >
      <Scrollbars universal>
        <StyledModalContainer>
          <StyledSummaryTitle isSmall>
            {t('common.summary')}
          </StyledSummaryTitle>
          <StyledSummaryMrr withMargin>
            {`${t('common.mrr')}: `}
            <span>{mrr?.toFixed(2)}</span>
          </StyledSummaryMrr>
          <Chart
            chartType="Line"
            width="100%"
            height="600px"
            data={createdAtChart.data}
            options={createdAtChart.options}
          />
          {otherCharts.map(({ data, options }) => (
            <Chart
              chartType="PieChart"
              data={data}
              options={options}
              width="100%"
              height="600px"
            />
          ))}
        </StyledModalContainer>
      </Scrollbars>
    </StyledModal>
  )
};
